import { environmentService } from '../environmentService';
import { getIdToken } from '../authToken';

import type {
  DocsHomeResponse,
  DocsProcess,
  DocsProcedure,
  GetProcessResponse,
  GetProcedureResponse,
  ImportJob,
  ImportItem,
  MermaidNodeBinding,
  ProcessRevision,
  ProcedureRevision,
  ProcedureProcessLink,
  SearchResponse,
} from './types';

interface ProcessEdge {
  id: string;
  sourceProcessId: string;
  targetProcessId: string;
  label: string | null;
}

interface DocsConfig {
  rootProcessId: string | null;
  docsEnabled: boolean;
}

class DocsService {
  private async getBaseURL(): Promise<string> {
    const config = await environmentService.getConfig();
    return config.apiUrl;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const baseURL = await this.getBaseURL();
    const url = `${baseURL}docs/${endpoint}`;

    const token = await getIdToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...((options.headers as Record<string, string>) || {}),
    };

    const response = await fetch(url, { ...options, headers });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `Request failed: ${response.status}`);
    }

    return data as T;
  }

  async getHome(): Promise<DocsHomeResponse> {
    return this.makeRequest<DocsHomeResponse>('home');
  }

  async search(
    query: string,
    type?: 'process' | 'procedure',
    limit?: number,
    offset?: number
  ): Promise<SearchResponse> {
    const params = new URLSearchParams({ q: query });
    if (type) params.set('type', type);
    if (limit) params.set('limit', String(limit));
    if (offset) params.set('offset', String(offset));
    return this.makeRequest<SearchResponse>(`search?${params.toString()}`);
  }

  async getProcess(idOrSlug: string): Promise<GetProcessResponse> {
    return this.makeRequest<GetProcessResponse>(
      `processes/${encodeURIComponent(idOrSlug)}`
    );
  }

  async createProcess(
    title: string,
    summary?: string
  ): Promise<{ success: boolean; process: DocsProcess }> {
    return this.makeRequest('processes', {
      method: 'POST',
      body: JSON.stringify({ title, summary }),
    });
  }

  async updateProcess(
    id: string,
    updates: { title?: string; summary?: string | null }
  ): Promise<{ success: boolean; process: DocsProcess }> {
    return this.makeRequest(`processes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async createProcessEdge(
    sourceProcessId: string,
    targetProcessId: string,
    label?: string
  ): Promise<{ success: boolean; edge: ProcessEdge }> {
    return this.makeRequest(`processes/${sourceProcessId}/edges`, {
      method: 'POST',
      body: JSON.stringify({ targetProcessId, label }),
    });
  }

  async deleteProcessEdge(
    processId: string,
    edgeId: string
  ): Promise<{ success: boolean }> {
    return this.makeRequest(`processes/${processId}/edges/${edgeId}`, {
      method: 'DELETE',
    });
  }

  async getMermaidBindings(
    processId: string
  ): Promise<{ success: boolean; bindings: MermaidNodeBinding[] }> {
    return this.makeRequest(`processes/${processId}/bindings`);
  }

  async updateMermaidBindings(
    processId: string,
    bindings: MermaidNodeBinding[]
  ): Promise<{ success: boolean; bindings: MermaidNodeBinding[] }> {
    return this.makeRequest(`processes/${processId}/bindings`, {
      method: 'PUT',
      body: JSON.stringify({ bindings }),
    });
  }

  async createDraftRevision(
    processId: string
  ): Promise<{ success: boolean; revision: ProcessRevision }> {
    return this.makeRequest(`processes/${processId}/draft`, {
      method: 'POST',
    });
  }

  async getProcessRevisions(
    processId: string
  ): Promise<{ success: boolean; revisions: ProcessRevision[] }> {
    return this.makeRequest(`processes/${processId}/revisions`);
  }

  async updateDraftRevision(
    processId: string,
    updates: { mermaidSource?: string; changeSummary?: string | null }
  ): Promise<{ success: boolean; revision: ProcessRevision }> {
    return this.makeRequest(`processes/${processId}/draft`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // ── Procedures ──────────────────────────────────────────────────────────

  async getProcedure(idOrSlug: string): Promise<GetProcedureResponse> {
    return this.makeRequest<GetProcedureResponse>(
      `procedures/${encodeURIComponent(idOrSlug)}`
    );
  }

  async createProcedure(
    title: string,
    type: string,
    summary?: string
  ): Promise<{ success: boolean; procedure: DocsProcedure }> {
    return this.makeRequest('procedures', {
      method: 'POST',
      body: JSON.stringify({ title, type, summary }),
    });
  }

  async updateProcedure(
    id: string,
    updates: { title?: string; summary?: string | null; type?: string }
  ): Promise<{ success: boolean; procedure: DocsProcedure }> {
    return this.makeRequest(`procedures/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async createProcedureDraft(
    procedureId: string
  ): Promise<{ success: boolean; revision: ProcedureRevision }> {
    return this.makeRequest(`procedures/${procedureId}/draft`, {
      method: 'POST',
    });
  }

  async updateProcedureDraft(
    procedureId: string,
    updates: { contentPlainText?: string; changeSummary?: string | null }
  ): Promise<{ success: boolean; revision: ProcedureRevision }> {
    return this.makeRequest(`procedures/${procedureId}/draft`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async linkProcedureProcesses(
    procedureId: string,
    processIds: string[]
  ): Promise<{ success: boolean; links: ProcedureProcessLink[] }> {
    return this.makeRequest(`procedures/${procedureId}/links`, {
      method: 'PUT',
      body: JSON.stringify({ processIds }),
    });
  }

  // ── Workflow ────────────────────────────────────────────────────────────

  async submitProcessRevision(
    processId: string
  ): Promise<{ success: boolean; revision: ProcessRevision }> {
    return this.makeRequest(`processes/${processId}/submit`, {
      method: 'POST',
    });
  }

  async approveProcessRevision(
    processId: string
  ): Promise<{ success: boolean; revision: ProcessRevision }> {
    return this.makeRequest(`processes/${processId}/approve`, {
      method: 'POST',
    });
  }

  async rejectProcessRevision(
    processId: string,
    reason: string
  ): Promise<{ success: boolean; revision: ProcessRevision }> {
    return this.makeRequest(`processes/${processId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async archiveProcess(processId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`processes/${processId}/archive`, {
      method: 'POST',
    });
  }

  async restoreProcess(processId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`processes/${processId}/restore`, {
      method: 'POST',
    });
  }

  async deleteProcess(processId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`processes/${processId}`, {
      method: 'DELETE',
    });
  }

  async submitProcedureRevision(
    procedureId: string
  ): Promise<{ success: boolean; revision: ProcedureRevision }> {
    return this.makeRequest(`procedures/${procedureId}/submit`, {
      method: 'POST',
    });
  }

  async approveProcedureRevision(
    procedureId: string
  ): Promise<{ success: boolean; revision: ProcedureRevision }> {
    return this.makeRequest(`procedures/${procedureId}/approve`, {
      method: 'POST',
    });
  }

  async rejectProcedureRevision(
    procedureId: string,
    reason: string
  ): Promise<{ success: boolean; revision: ProcedureRevision }> {
    return this.makeRequest(`procedures/${procedureId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async archiveProcedure(procedureId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`procedures/${procedureId}/archive`, {
      method: 'POST',
    });
  }

  async restoreProcedure(procedureId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`procedures/${procedureId}/restore`, {
      method: 'POST',
    });
  }

  async deleteProcedure(procedureId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`procedures/${procedureId}`, {
      method: 'DELETE',
    });
  }

  async getReviewQueue(): Promise<{
    success: boolean;
    processes: DocsProcess[];
    procedures: DocsProcedure[];
  }> {
    return this.makeRequest('admin/review-queue');
  }

  // ── Imports ─────────────────────────────────────────────────────────────

  async requestUploadUrl(
    filename: string,
    contentType: string
  ): Promise<{ success: boolean; uploadUrl: string; s3Key: string }> {
    return this.makeRequest('uploads/request', {
      method: 'POST',
      body: JSON.stringify({ filename, contentType }),
    });
  }

  async createImportJob(
    mode: 'single' | 'bulk',
    sourceS3Key: string
  ): Promise<{ success: boolean; job: ImportJob }> {
    return this.makeRequest('imports', {
      method: 'POST',
      body: JSON.stringify({ mode, sourceS3Key }),
    });
  }

  async getImportJob(
    jobId: string
  ): Promise<{ success: boolean; job: ImportJob; items: ImportItem[] }> {
    return this.makeRequest(`imports/${jobId}`);
  }

  async getDocsConfig(): Promise<{
    success: boolean;
    config: { rootProcessId: string | null; docsEnabled: boolean };
  }> {
    return this.makeRequest('admin/config');
  }

  async updateDocsConfig(config: {
    rootProcessId?: string | null;
    docsEnabled?: boolean;
  }): Promise<{ success: boolean; config: DocsConfig }> {
    return this.makeRequest('admin/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }
}

export const docsService = new DocsService();
